import { useCallback, useEffect, useRef, useState } from 'react';
import type { LatencyHistoryRow } from '../lib/api.js';
import { fetchLatencyHistory } from '../lib/api.js';
import {
  type DashboardResource,
  createDashboardResource,
  markResourceError,
  markResourceLoading,
  markResourceSuccess,
  updateResourceStaleness,
} from '../lib/dashboardResource.js';

interface UseLatencyHistoryOptions {
  region?: string;
  hours?: number;
  pollIntervalMs?: number;
  staleAfterMs?: number;
}

interface UseLatencyHistoryResult {
  history: DashboardResource<LatencyHistoryRow[]>;
  refreshHistory: () => Promise<void>;
}

export function useLatencyHistory({
  region,
  hours = 24,
  pollIntervalMs = 60_000,
  staleAfterMs = 90_000,
}: UseLatencyHistoryOptions = {}): UseLatencyHistoryResult {
  const [history, setHistory] = useState(createDashboardResource<LatencyHistoryRow[]>());
  const mountedRef = useRef(true);

  const refreshHistory = useCallback(async () => {
    if (!mountedRef.current) return;
    setHistory((resource) => markResourceLoading(resource));
    try {
      const payload = await fetchLatencyHistory({ region, hours });
      if (!mountedRef.current) return;
      setHistory((resource) => markResourceSuccess(resource, payload, new Date().toISOString()));
    } catch (error) {
      if (!mountedRef.current) return;
      setHistory((resource) => markResourceError(resource, error));
    }
  }, [hours, region]);

  useEffect(() => {
    mountedRef.current = true;
    refreshHistory();
    const poller = setInterval(() => {
      refreshHistory();
    }, pollIntervalMs);
    const staleness = setInterval(() => {
      setHistory((resource) => updateResourceStaleness(resource, staleAfterMs));
    }, 1_000);

    return () => {
      mountedRef.current = false;
      clearInterval(poller);
      clearInterval(staleness);
    };
  }, [hours, region, pollIntervalMs, staleAfterMs, refreshHistory]);

  return { history, refreshHistory };
}
